/**
 * L'état de « Gérer une séance » : l'étape, son chemin (pour « retour »), chaque choix, et la boîte
 * ouverte. Pur — le crochet (`useTutorManageSessionState`) y ajoute les lectures, les écritures et la
 * navigation.
 *
 * L'enchaînement est celui des étapes visibles de l'ancien `FormStepper` (`ManageRDVScreen`) :
 *  - le retard : la situation → combien → la confirmation (et sa boîte) ;
 *  - l'absence : la situation → « Confirme ton absence » → le rattrapage, sauf quand il est sauté →
 *    le message ;
 *  - un individuel : la situation → la nouvelle date → la raison.
 */
import { type TutorManageSessionDialogKind } from "../../../../../../design-system/templates/TutorManageSessionDialog"
import { type TutorManageSessionStep } from "../../../../../../design-system/templates/TutorManageSessionFlow"
import { type TutorMoveKind, type TutorNewSlot } from "../../../../../../design-system/organisms/TutorNewDateStep"
import { type UITutorAbsenceImpactStudent } from "../../../types/tutorLives"

export type TutorManageSessionUi = {
    step: TutorManageSessionStep
    /** Les étapes déjà passées, la plus récente en dernier : « retour » y revient. */
    history: TutorManageSessionStep[]
    lateMinutes: number | null
    absenceConfirmed: boolean
    makeupIds: string[]
    message: string
    instructions: string
    excuseConfirmed: boolean
    moveKind: TutorMoveKind
    newDate: string
    newSlot: TutorNewSlot
    reasonId: string | null
    parentMessage: string
    studentValidated: boolean
    dialog: TutorManageSessionDialogKind | null
    /** L'impact renvoyé par un refus de la déclaration : il remplace celui de l'aperçu. */
    impactOverride: UITutorAbsenceImpactStudent[] | null
    /** « Je dois m'absenter » touché avant que le décompte soit lu : l'étape s'ouvre à son arrivée. */
    awaitingAbsence: boolean
    /** Le refus du serveur, en tête de la feuille (l'ancien `serverError` du `FormStepper`). */
    errorMessage: string | null
}

export const initialTutorManageSessionUi: TutorManageSessionUi = {
    step: "situation",
    history: [],
    lateMinutes: null,
    absenceConfirmed: false,
    makeupIds: [],
    message: "",
    instructions: "",
    excuseConfirmed: false,
    moveKind: "one_occurrence",
    newDate: "",
    newSlot: { weekday: null, time: null },
    reasonId: null,
    parentMessage: "",
    studentValidated: false,
    dialog: null,
    impactOverride: null,
    awaitingAbsence: false,
    errorMessage: null,
}

export type TutorManageSessionAction =
    | { type: "GO"; step: TutorManageSessionStep }
    | { type: "BACK" }
    | { type: "DONE" }
    | { type: "AWAIT_ABSENCE" }
    | { type: "START_MOVE"; moveKind: TutorMoveKind; newDate: string; newSlot: TutorNewSlot }
    | { type: "SET"; patch: Partial<Omit<TutorManageSessionUi, "step" | "history" | "dialog">> }
    | { type: "TOGGLE_MAKEUP"; id: string }
    | { type: "OPEN_DIALOG"; dialog: TutorManageSessionDialogKind }
    | { type: "CLOSE_DIALOG" }
    | { type: "IMPACT_CHANGED"; impact: UITutorAbsenceImpactStudent[] }

export function tutorManageSessionReducer(
    state: TutorManageSessionUi,
    action: TutorManageSessionAction
): TutorManageSessionUi {
    switch (action.type) {
        case "GO":
            if (action.step === state.step) return state
            return {
                ...state,
                step: action.step,
                history: [...state.history, state.step],
                awaitingAbsence: false,
                errorMessage: null,
            }
        case "BACK": {
            const previous = state.history.at(-1)
            if (previous == null) return state
            return { ...state, step: previous, history: state.history.slice(0, -1), errorMessage: null }
        }
        case "DONE":
            return { ...state, step: "done", history: [], dialog: null, errorMessage: null }
        case "AWAIT_ABSENCE":
            return { ...state, awaitingAbsence: true }
        case "START_MOVE":
            return {
                ...state,
                moveKind: action.moveKind,
                newDate: action.newDate,
                newSlot: action.newSlot,
                reasonId: null,
                step: "newDate",
                history: [...state.history, state.step],
            }
        case "SET":
            return { ...state, ...action.patch }
        case "TOGGLE_MAKEUP":
            return {
                ...state,
                makeupIds: state.makeupIds.includes(action.id)
                    ? state.makeupIds.filter((id) => id !== action.id)
                    : [...state.makeupIds, action.id],
            }
        case "OPEN_DIALOG":
            return { ...state, dialog: action.dialog }
        case "CLOSE_DIALOG":
            return state.dialog == null ? state : { ...state, dialog: null }
        case "IMPACT_CHANGED":
            return { ...state, impactOverride: action.impact, dialog: { kind: "impactChanged" } }
        default:
            return state
    }
}
